import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { ExportDataTypeInclusionSelector } from "./ExportDataTypeInclusionSelector";
import { FieldConfigurationSelector } from "./FieldConfigurationSelector";
import {
  firmographicsExportFields,
  mergersAcquisitionsExportFields,
} from "./exportConfigurationFields";
import { EXPORT_INCLUSION_TYPES, type ExportFieldConfiguration } from "./types";
import {
  DateField,
  DiscoveredDateFilter,
  MultiSelect,
  NullPolicySelect,
} from "./FinancingEventsExportOptions";
import {
  MERGERS_ACQUISITIONS_CATEGORY_OPTIONS,
  type MergersAcquisitionsFilters,
} from "./mergersAcquisitions";

const DATA_TYPE = "M&A Events";
const ID = "mergers-acquisitions";

interface Props {
  inclusion: EXPORT_INCLUSION_TYPES;
  setInclusion: (value: EXPORT_INCLUSION_TYPES) => void;
  filters: MergersAcquisitionsFilters;
  setFilters: (filters: MergersAcquisitionsFilters) => void;
  fieldConfiguration: ExportFieldConfiguration;
  setFieldConfiguration: (cfg: ExportFieldConfiguration) => void;
  enforceMandatoryFields?: boolean;
}

export const MergersAcquisitionsExportOptions: React.FC<Props> = ({
  inclusion,
  setInclusion,
  filters,
  setFilters,
  fieldConfiguration,
  setFieldConfiguration,
  enforceMandatoryFields,
}) => {
  const disabled = inclusion !== EXPORT_INCLUSION_TYPES.ALL;

  return (
    <Stack spacing={2} sx={{ width: "70%", paddingTop: "40px" }}>
      <Typography variant="h5" fontWeight={500} fontSize={16}>
        M&A Events
      </Typography>
      <ExportDataTypeInclusionSelector
        dataType={DATA_TYPE}
        options={[
          {
            label: "Do not include any M&A Events",
            value: EXPORT_INCLUSION_TYPES.NONE,
          },
          {
            label: "Include M&A Events",
            value: EXPORT_INCLUSION_TYPES.ALL,
          },
        ]}
        inclusion={inclusion}
        setInclusion={setInclusion}
      />

      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Category</Typography>
        <MultiSelect
          id={`${ID}-category-include`}
          label="Include"
          placeholder="Any category"
          options={MERGERS_ACQUISITIONS_CATEGORY_OPTIONS}
          value={filters.category?.include ?? []}
          onChange={(include) =>
            setFilters({
              ...filters,
              category: { ...filters.category, include },
            })
          }
          disabled={disabled}
          loading={false}
        />
        <MultiSelect
          id={`${ID}-category-exclude`}
          label="Exclude"
          placeholder="Nothing excluded"
          options={MERGERS_ACQUISITIONS_CATEGORY_OPTIONS}
          value={filters.category?.exclude ?? []}
          onChange={(exclude) =>
            setFilters({
              ...filters,
              category: { ...filters.category, exclude },
            })
          }
          disabled={disabled}
          loading={false}
        />
        <Typography variant="caption" color="text.secondary">
          A category in both lists is excluded.
        </Typography>
      </Stack>

      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Effective Date</Typography>
        <Stack direction="row" spacing={2}>
          <DateField
            id={`${ID}-effective-start`}
            label="From"
            value={filters.effectiveDate?.start}
            onChange={(start) =>
              setFilters({
                ...filters,
                effectiveDate: { ...filters.effectiveDate, start },
              })
            }
            disabled={disabled}
          />
          <DateField
            id={`${ID}-effective-end`}
            label="To"
            value={filters.effectiveDate?.end}
            onChange={(end) =>
              setFilters({
                ...filters,
                effectiveDate: { ...filters.effectiveDate, end },
              })
            }
            disabled={disabled}
          />
        </Stack>
        <Typography variant="caption" color="warning.main">
          Effective date is frequently not reported. Use Discovered Date for a
          more reliable view of recency.
        </Typography>
        <NullPolicySelect
          id={`${ID}-effective-null-policy`}
          label="Records with no effective date"
          value={filters.effectiveDate?.nullPolicy}
          onChange={(nullPolicy) =>
            setFilters({
              ...filters,
              effectiveDate: { ...filters.effectiveDate, nullPolicy },
            })
          }
          disabled={disabled}
        />
      </Stack>

      <DiscoveredDateFilter
        idPrefix={ID}
        value={filters.discoveredDate}
        onChange={(discoveredDate) =>
          setFilters({ ...filters, discoveredDate })
        }
        disabled={disabled}
      />

      <Box>
        <FieldConfigurationSelector
          fields={mergersAcquisitionsExportFields}
          firmographicsFields={firmographicsExportFields}
          value={fieldConfiguration}
          onChange={setFieldConfiguration}
          title="M&A Events Fields"
          checkboxLabel="Export All M&A Events Fields"
          requiredField={
            enforceMandatoryFields
              ? {
                  values: ["id"],
                  note: "Note: Event ID will always be included in your export for proper M&A event identification.",
                }
              : undefined
          }
          disabled={disabled}
        />
      </Box>
    </Stack>
  );
};
